import React, { useState, useEffect } from 'react';
import { NodeWithRelationships, KnowledgeAnswer } from './types';
import { seaOfKnowledgeService } from '../../services/seaOfKnowledgeService';
import './NodeDetail.css';

interface NodeDetailProps {
  node: NodeWithRelationships;
  onClose: () => void;
  onNodeUpdated: () => void;
  onGenerateNode: () => void;
}

const NodeDetail: React.FC<NodeDetailProps> = ({
  node,
  onClose,
  onNodeUpdated,
  onGenerateNode
}) => {
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState<KnowledgeAnswer | null>(node.latestAnswer);
  const [review, setReview] = useState<{ review: string; score: number; approved: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    if (node.latestAnswer) {
      setCurrentAnswer(node.latestAnswer);
      if (node.latestAnswer.ai_review && node.latestAnswer.ai_score !== null) {
        setReview({
          review: node.latestAnswer.ai_review,
          score: node.latestAnswer.ai_score,
          approved: node.latestAnswer.is_approved
        });
        setShowRetry(!node.latestAnswer.is_approved);
      }
    } else {
      setCurrentAnswer(null);
      setReview(null);
      setShowRetry(false);
    }
  }, [node]);

  const handleSubmitAnswer = async () => {
    if (!answerText.trim()) {
      setError('Please enter an answer');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await seaOfKnowledgeService.submitAnswer(node.id, answerText);
      if (result.success && result.data && result.data.answer) {
        setCurrentAnswer(result.data.answer);
        setAnswerText('');
        // Automatically get AI review
        await handleReviewAnswer(result.data.answer.id, answerText);
      } else {
        setError(result.message || 'Failed to submit answer');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewAnswer = async (answerId?: number, text?: string) => {
    setReviewing(true);
    setError(null);

    try {
      const result = await seaOfKnowledgeService.reviewAnswer(
        node.id,
        answerId,
        text || answerText
      );
      if (result.success) {
        setReview({
          review: result.review || '',
          score: result.score || 0,
          approved: result.approved || false
        });
        setShowRetry(!result.approved);
        await onNodeUpdated();
      } else {
        setError(result.message || 'Failed to review answer');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to review answer');
    } finally {
      setReviewing(false);
    }
  };

  const handleRetry = () => {
    setAnswerText('');
    setReview(null);
    setShowRetry(false);
    setCurrentAnswer(null);
  };

  const handleSkip = () => {
    // Just close the detail panel or move to next node
    onClose();
  };

  const getStatusColor = () => {
    switch (node.status) {
      case 'mastered':
        return '#28a745';
      case 'answered':
        return '#d4edda';
      default:
        return '#ffffff';
    }
  };

  const getCircleColor = () => {
    switch (node.status) {
      case 'mastered':
        return '#28a745';
      case 'answered':
        return '#d4edda';
      default:
        return '#333333';
    }
  };

  const getStatusText = () => {
    switch (node.status) {
      case 'mastered':
        return 'Mastered';
      case 'answered':
        return 'Answered';
      default:
        return 'Pending';
    }
  };

  return (
    <div className="node-detail">
      <div className="node-detail-header">
        <button className="node-detail-close" onClick={onClose} title="Close">
          ×
        </button>
      </div>

      <div className="node-detail-content">
        <div className="node-detail-visual-section">
          <div className="node-detail-circle" style={{ backgroundColor: getCircleColor() }}>
          </div>
          <div className="node-detail-status-indicators">
            <button 
              className={`node-detail-status-btn ${node.status === 'pending' ? 'active' : ''}`}
              disabled
            >
              <span className="status-indicator pending"></span>
              <span>Pending</span>
            </button>
            <button 
              className={`node-detail-status-btn ${node.status === 'answered' ? 'active' : ''}`}
              disabled
            >
              <span className="status-indicator answered"></span>
              <span>Answered</span>
            </button>
            <button 
              className={`node-detail-status-btn ${node.status === 'mastered' ? 'active' : ''}`}
              disabled
            >
              <span className="status-indicator mastered"></span>
              <span>Mastered</span>
            </button>
          </div>
          <div className="node-detail-action-buttons">
            <button className="node-detail-action-icon" title="Add" onClick={onGenerateNode}>
              +
            </button>
            <button className="node-detail-action-icon" title="Remove" onClick={onClose}>
              −
            </button>
            <button className="node-detail-action-icon" title="Home" onClick={onClose}>
              ⌂
            </button>
          </div>
        </div>

        <div className="node-detail-question">
          <h2>{node.question}</h2>
          {node.description && (
            <p className="node-detail-description">{node.description}</p>
          )}
        </div>

        {node.outgoingEdges.length > 0 && (
          <div className="node-detail-related">
            <h3>Related Questions</h3>
            <ul>
              {node.outgoingEdges.map(edge => (
                <li key={edge.id}>{edge.to_node_question}</li>
              ))}
            </ul>
          </div>
        )}

        {!currentAnswer && !review && (
          <div className="node-detail-answer-section">
            <h3>Your Answer</h3>
            <div className="node-detail-answer-input-wrapper">
              <textarea
                className="node-detail-answer-input"
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                placeholder="Enter your answer here..."
                rows={8}
              />
              <div className="node-detail-floating-actions">
                <button className="node-detail-floating-btn" title="AI Assistant">
                  💬
                </button>
                <button className="node-detail-floating-btn" title="Keyboard">
                  ⌨
                </button>
              </div>
            </div>
            {error && <div className="node-detail-error">{error}</div>}
            <div className="node-detail-answer-actions">
              <button className="node-detail-status-badge" onClick={onClose}>
                Pending
              </button>
              <button
                className="node-detail-submit-btn"
                onClick={handleSubmitAnswer}
                disabled={submitting || !answerText.trim()}
              >
                {submitting ? 'Submitting...' : 'Submit Answer'}
              </button>
            </div>
          </div>
        )}

        {reviewing && (
          <div className="node-detail-reviewing">
            <p>AI is reviewing your answer...</p>
          </div>
        )}

        {review && (
          <div className="node-detail-review-section">
            <h3>AI Review</h3>
            <div className="node-detail-score">
              Score: <strong>{review.score}/100</strong>
              {review.approved && (
                <span className="node-detail-approved">✓ Approved</span>
              )}
            </div>
            <div className="node-detail-review-text">{review.review}</div>

            {showRetry && (
              <div className="node-detail-actions">
                <button
                  className="node-detail-action-btn retry"
                  onClick={handleRetry}
                >
                  Retry
                </button>
                <button
                  className="node-detail-action-btn skip"
                  onClick={handleSkip}
                >
                  Skip
                </button>
              </div>
            )}

            {review.approved && (
              <div className="node-detail-actions">
                <button
                  className="node-detail-action-btn generate"
                  onClick={onGenerateNode}
                >
                  Generate Related Node
                </button>
                <button
                  className="node-detail-action-btn skip"
                  onClick={handleSkip}
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        )}

        {currentAnswer && !review && (
          <div className="node-detail-answer-display">
            <h3>Your Answer</h3>
            <div className="node-detail-answer-text">{currentAnswer.answer_text}</div>
            <button
              className="node-detail-submit-btn"
              onClick={() => handleReviewAnswer(currentAnswer.id)}
              disabled={reviewing}
            >
              {reviewing ? 'Reviewing...' : 'Get AI Review'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeDetail;

